import React, { useMemo, useCallback } from 'react';
import { GeoJSON } from 'react-leaflet';
import { Place } from '@m4a3/api-client';
import type { ClickMode } from '../../models/click-mode';

interface PlaceLayerProps {
  places: Place[];
  selectedPlaceId: string | null;
  placeStatuses: Map<string, { enabled: boolean; name?: string }>;
  placesUpdateKey: number;
  clickMode: ClickMode;
  onPlaceClick: (place: Place) => void;
  enablePopup?: boolean;
  disableTooltip?: boolean;
}

// Helper function to handle GeoJSON that might be string or object
const parseGeoJSON = (geojson: string | object): any => {
  if (typeof geojson === 'string') {
    return JSON.parse(geojson);
  }
  return geojson;
};

// Memoized style function to prevent recalculation
const createStyleFunction = (
  selectedPlaceId: string | null,
  placeStatuses: Map<string, { enabled: boolean; name?: string }>
) => {
  return (feature: any) => {
    const isSelected = feature?.properties?.id === selectedPlaceId;
    const placeId = feature?.properties?.id;
    const status = placeStatuses.get(placeId);
    
    // Fill color: Always preserve enabled/disabled state
    let fillColor = '#3388ff'; // Default blue for unknown status
    if (status) {
      if (status.enabled) {
        fillColor = '#28a745'; // Green for enabled
      } else {
        fillColor = '#dc3545'; // Red for disabled
      }
    }
    
    // Border styling: Special treatment for selected places
    let borderColor = '#3388ff'; // Default blue border
    let borderWeight = 2;
    let dashArray = '';
    
    if (isSelected) {
      borderColor = '#1a1a1a'; // Dark border for selected
      borderWeight = 5; // Thicker border
      dashArray = ''; // Solid line for selected
    }
    
    return {
      color: borderColor,
      weight: borderWeight,
      dashArray,
      fillOpacity: isSelected ? 0.5 : 0.2, // Slightly more opaque when selected
      fillColor,
    };
  };
};

// Memoized tooltip creation function
const createTooltipContent = (
  placeId: string,
  places: Place[],
  placeStatuses: Map<string, { enabled: boolean; name?: string }>
): string => {
  const statusInfo = placeStatuses.get(placeId);
  const place = places.find(p => p.id === placeId);
  
  let tooltipContent = `Place ID: ${placeId}`;
  if (statusInfo?.name) {
    tooltipContent = `${statusInfo.name}`;
  }
  if (place?.placeType) {
    tooltipContent += `\nType: ${place.placeType}`;
  }
  if (statusInfo) {
    tooltipContent += `\nStatus: ${statusInfo.enabled ? 'Enabled' : 'Disabled'}`;
  }
  
  return tooltipContent;
};

const PlaceLayer = React.memo(({
  places,
  selectedPlaceId,
  placeStatuses,
  placesUpdateKey,
  clickMode,
  onPlaceClick,
  enablePopup = false,
  disableTooltip = false,
}: PlaceLayerProps): JSX.Element | null => {
  // Create GeoJSON features for places with proper memoization
  const placeFeatures = useMemo(() => {
    return places.map(place => {
      try {
        const geometry = parseGeoJSON(place.geometry);
        return {
          type: 'Feature' as const,
          properties: {
            id: place.id,
            placeType: place.placeType,
            areaSqm: place.areaSqm,
          },
          geometry,
        };
      } catch (error) {
        console.error('Error parsing place geometry:', place.id, error);
        return null;
      }
    }).filter(Boolean);
  }, [places]);

  const placesGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: placeFeatures,
  }), [placeFeatures]);

  // Compute a lightweight hash of place IDs to capture dataset changes
  const placesIdHash = useMemo(() => {
    let hash = 0 >>> 0;
    for (const place of places) {
      const id = place.id || '';
      for (let i = 0; i < id.length; i++) {
        hash = (Math.imul(hash, 31) + id.charCodeAt(i)) >>> 0;
      }
    }
    return hash;
  }, [places]);

  // Force a remount when the underlying data changes size to avoid stale GeoJSON layers
  const layerKey = useMemo(() => `places-${placesUpdateKey}-${selectedPlaceId}-${placeStatuses.size}-${places.length}-${placesIdHash}`, [
    placesUpdateKey,
    selectedPlaceId,
    placeStatuses.size,
    places.length,
    placesIdHash,
  ]);

  // Memoize the style function
  const styleFunction = useMemo(() => {
    return createStyleFunction(selectedPlaceId, placeStatuses);
  }, [selectedPlaceId, placeStatuses]);

  // Memoize the onEachFeature function
  const onEachFeature = useCallback((feature: any, layer: any) => {
    const placeId = feature.properties.id;
    
    // Create tooltip content
    const tooltipContent = createTooltipContent(placeId, places, placeStatuses);
    
    // Helper to (re)bind tooltip
    const bindTooltip = () => {
      try {
        layer.bindTooltip(tooltipContent, {
          permanent: false,
          direction: 'top',
          offset: [0, -10],
          className: 'building-tooltip'
        });
      } catch {}
    };

    // Initially bind tooltip (unless disabled)
    if (!disableTooltip) {
      bindTooltip();
    }
    
    layer.on({
      click: (e: any) => {
        // Prevent map click-through
        e?.originalEvent?.stopPropagation?.();
        e?.originalEvent?.preventDefault?.();

        // Optionally show popup with details
        if (enablePopup) {
          const statusInfo = placeStatuses.get(placeId);
          const place = places.find(p => p.id === placeId);
          const friendlyName = statusInfo?.name || place?.friendlyName || placeId;
          const enabled = statusInfo?.enabled ?? place?.enabled;
          const popupHtml = `
            <div style="font-size:12px; line-height:1.3;">
              <div style="font-weight:600; color:#111827;">${friendlyName}</div>
              <div style="color:#374151;">Status: ${enabled ? 'Enabled' : 'Disabled'}</div>
              <div style="color:#6B7280; font-size:11px;">ID: ${placeId}</div>
            </div>
          `;
          try {
            // Hide tooltip while popup is open
            if (!disableTooltip) {
              try { layer.unbindTooltip(); } catch {}
            }
            layer.bindPopup(popupHtml, { offset: [0, -8], className: 'building-popup' }).openPopup();
            // Restore tooltip after popup closes
            if (!disableTooltip) {
              layer.once('popupclose', () => {
                bindTooltip();
              });
            }
          } catch {}
        }

        if (clickMode === 'select') {
          const place = places.find(p => p.id === placeId);
          if (place) {
            onPlaceClick(place);
          } else {
            console.error('Place not found for ID:', placeId);
          }
        }
      },
      mouseover: () => {
        if ('setStyle' in layer) {
          const isSelected = feature.properties.id === selectedPlaceId;
          (layer as any).setStyle({
            fillOpacity: isSelected ? 0.7 : 0.4,
            weight: isSelected ? 6 : 3,
          });
        }
      },
      mouseout: () => {
        if ('setStyle' in layer) {
          const isSelected = feature.properties.id === selectedPlaceId;
          (layer as any).setStyle({
            fillOpacity: isSelected ? 0.5 : 0.2,
            weight: isSelected ? 5 : 2,
          });
        }
      },
    });
  }, [places, placeStatuses, selectedPlaceId, clickMode, onPlaceClick]);

  if (placesGeoJSON.features.length === 0) {
    return null;
  }

  return (
    <GeoJSON
      key={layerKey}
      data={placesGeoJSON}
      style={styleFunction}
      onEachFeature={onEachFeature}
    />
  );
});

PlaceLayer.displayName = 'PlaceLayer';

export default PlaceLayer;
